/**
 * The dark header: the layered wordmark as a link to the main content, the
 * language switch and the header call to action. Nothing else — no
 * workspace, no sign-in: the page has one action, and this is one of its
 * two doors to it.
 */

import type { MouseEvent, ReactNode } from 'react';

import type { Language } from '../../i18n/translations.js';
import { BrandMark } from './BrandMark.js';
import type { PublicEarlyAccessCopy } from './copy.js';
import { LanguageIcon } from './icons.js';

export function PublicEarlyAccessHeader({
  copy,
  language,
  mainId,
  onToggleLanguage,
  onRequestAccess,
}: {
  readonly copy: PublicEarlyAccessCopy;
  readonly language: Language;
  readonly mainId: string;
  readonly onToggleLanguage: () => void;
  readonly onRequestAccess: (from: HTMLElement) => void;
}): ReactNode {
  const focusMain = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(mainId)?.focus({ preventScroll: true });
  };

  return (
    <header className="cs-site-header">
      <div className="cs-header-inner">
        <a
          className="cs-brand"
          href={`#${mainId}`}
          aria-label="Mouja"
          onClick={focusMain}
        >
          <BrandMark language={language} />
        </a>
        <div className="cs-header-actions">
          <button
            type="button"
            className="cs-lang-button"
            lang={copy.langLang}
            aria-label={copy.langAria}
            onClick={onToggleLanguage}
          >
            <LanguageIcon />
            <span>{copy.langLabel}</span>
          </button>
          <button
            type="button"
            className="cs-nav-cta"
            aria-label={copy.navCta}
            onClick={(event) => {
              onRequestAccess(event.currentTarget);
            }}
          >
            {copy.navCta}
          </button>
        </div>
      </div>
    </header>
  );
}
